import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Zap, Settings2, X, Pencil, Trash2, Plus, Loader2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

// Búsqueda sin tildes ni mayúsculas
const norm = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Reemplaza {nombre} y {asesor}. Sin nombre del cliente, se quita la variable
 * y se arreglan los espacios ("Hola {nombre}," → "Hola,").
 */
export function applyVariables(text, { contactName, agentName }) {
  const first = (contactName || '').trim().split(/\s+/)[0] || '';
  return text
    .replace(/\{nombre\}/gi, first)
    .replace(/\{asesor\}/gi, (agentName || '').trim().split(/\s+/)[0] || '')
    .replace(/ +([,.!?;:])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ');
}

/** Lista de respuestas (las de todos y las propias), con recarga. */
export function useQuickReplies() {
  const { apiFetch } = useApi();
  const [items, setItems] = useState(null);
  const reload = useCallback(() => apiFetch('/api/quick-replies').then(setItems).catch(() => setItems([])), [apiFetch]);
  useEffect(() => { reload(); }, [reload]);
  return { items: items || [], loaded: items !== null, reload };
}

export function filterReplies(items, query) {
  const q = norm(query.trim());
  if (!q) return items;
  return items.filter(r => norm(r.title).includes(q) || norm(r.body).includes(q));
}

/** Lista flotante sobre la caja de texto. index = resaltado (teclado). */
export function QuickReplyPicker({ items, query, index, onPick, onManage, onClose }) {
  return (
    <div style={st.picker} role="listbox" aria-label="Respuestas rápidas">
      <div style={st.pickerHead}>
        <Zap size={14} color="var(--accent)" />
        <span style={{ flex: 1 }}>Respuestas rápidas{query ? <span style={st.muted}> · “{query}”</span> : null}</span>
        <span style={st.muted}>↑↓ elegir · Enter insertar · Esc cerrar</span>
        <button type="button" style={st.iconBtn} onClick={onClose} aria-label="Cerrar"><X size={14} /></button>
      </div>
      <div style={st.pickerList}>
        {items.length === 0 && (
          <div style={st.empty}>{query ? 'Ninguna coincide.' : 'Aún no hay respuestas rápidas.'} Créalas en “Administrar”.</div>
        )}
        {items.map((r, i) => (
          <button key={r.id} type="button" role="option" aria-selected={i === index}
            style={{ ...st.item, ...(i === index ? st.itemActive : {}) }}
            onMouseDown={e => { e.preventDefault(); onPick(r); }}>
            <span style={st.itemTitle}>{r.title}
              <span style={r.scope === 'global' ? st.chipGlobal : st.chipMine}>{r.scope === 'global' ? 'Para todos' : 'Mía'}</span>
            </span>
            <span style={st.itemBody}>{r.body}</span>
          </button>
        ))}
      </div>
      <button type="button" style={st.manage} onMouseDown={e => { e.preventDefault(); onManage(); }}>
        <Settings2 size={13} /> Administrar respuestas
      </button>
    </div>
  );
}

/** Crear, editar y borrar respuestas (las propias; el admin también las de todos). */
export function QuickReplyManager({ items, isAdmin, onClose, onChanged }) {
  const { apiFetch } = useApi();
  const empty = { id: null, title: '', body: '', scope: isAdmin ? 'global' : 'mine' };
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const editableItems = useMemo(() => items.filter(r => r.scope === 'mine' || isAdmin), [items, isAdmin]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const body = JSON.stringify({ title: form.title, body: form.body, scope: form.scope });
      if (form.id) await apiFetch(`/api/quick-replies/${form.id}`, { method: 'PUT', body });
      else await apiFetch('/api/quick-replies', { method: 'POST', body });
      setForm(empty);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(r) {
    if (!window.confirm(`¿Borrar la respuesta "${r.title}"?`)) return;
    try {
      await apiFetch(`/api/quick-replies/${r.id}`, { method: 'DELETE' });
      if (form.id === r.id) setForm(empty);
      await onChanged();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="qr-title">
        <div style={st.modalHead}>
          <Zap size={18} color="var(--accent)" />
          <h2 id="qr-title" style={st.title}>Respuestas rápidas</h2>
          <button type="button" style={st.iconBtn} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <p style={st.help}>
          Textos que insertas en el chat (botón ⚡ o escribiendo <code>/</code>), revisas y envías. Puedes usar
          <code>{' {nombre} '}</code> (nombre del cliente) y <code>{' {asesor} '}</code> (tu nombre).
          Solo sirven dentro de las 24 h; fuera de ese plazo se usan las plantillas de Meta.
        </p>

        <form onSubmit={save} style={st.form}>
          <div style={st.row}>
            <input style={{ ...st.input, flex: 1 }} placeholder="Título (ej. Precios del diplomado)" maxLength={60}
              value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} aria-label="Título" />
            {isAdmin && !form.id && (
              <select style={st.input} value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))} aria-label="Para quién">
                <option value="global">Para todos</option>
                <option value="mine">Solo mía</option>
              </select>
            )}
          </div>
          <textarea style={{ ...st.input, minHeight: 90, resize: 'vertical' }} maxLength={2000} placeholder="Hola {nombre}, el diplomado cuesta…"
            value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} aria-label="Texto" />
          {error && <div style={st.error} role="alert">{error}</div>}
          <div style={st.row}>
            {form.id && <button type="button" className="ui-hover" style={st.secondary} onClick={() => setForm(empty)}>Cancelar edición</button>}
            <div style={{ flex: 1 }} />
            <button type="submit" className="ui-primary" style={st.primary} disabled={busy || !form.title.trim() || !form.body.trim()}>
              {busy ? <Loader2 size={14} className="ui-spin" /> : form.id ? <Pencil size={14} /> : <Plus size={14} />}
              {form.id ? 'Guardar cambios' : 'Agregar'}
            </button>
          </div>
        </form>

        <div style={st.list}>
          {editableItems.length === 0 && <div style={st.empty}>Todavía no hay respuestas.</div>}
          {editableItems.map(r => (
            <div key={r.id} style={st.listItem}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={st.itemTitle}>{r.title}
                  <span style={r.scope === 'global' ? st.chipGlobal : st.chipMine}>{r.scope === 'global' ? 'Para todos' : 'Mía'}</span>
                </div>
                <div style={{ ...st.itemBody, WebkitLineClamp: 3 }}>{r.body}</div>
              </div>
              <button type="button" className="ui-hover" style={st.iconBtn} onClick={() => setForm({ id: r.id, title: r.title, body: r.body, scope: r.scope })} aria-label={`Editar ${r.title}`}><Pencil size={14} /></button>
              <button type="button" className="ui-hover" style={{ ...st.iconBtn, color: 'var(--danger)' }} onClick={() => remove(r)} aria-label={`Borrar ${r.title}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const st = {
  picker: { position: 'absolute', left: 0, right: 0, bottom: 'calc(100% + 8px)', background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-md)', zIndex: 30, display: 'flex', flexDirection: 'column', maxHeight: 320, overflow: 'hidden' },
  pickerHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid var(--border)', fontSize: 12.5, fontWeight: 700, color: 'var(--text)' },
  pickerList: { overflowY: 'auto', padding: 4, flex: 1 },
  item: { display: 'flex', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: 'var(--text)' },
  itemActive: { background: 'var(--accent-soft)' },
  itemTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 },
  itemBody: { fontSize: 12.5, color: 'var(--text-2)', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' },
  chipGlobal: { fontSize: 10, fontWeight: 700, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', borderRadius: 999, padding: '0 6px' },
  chipMine: { fontSize: 10, fontWeight: 700, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 999, padding: '0 6px' },
  manage: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', border: 'none', padding: '8px', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer' },
  empty: { fontSize: 12.5, color: 'var(--text-3)', padding: '10px 12px' },
  muted: { color: 'var(--text-3)', fontWeight: 500 },
  iconBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 4, borderRadius: 6 },
  overlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 },
  modal: { background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 620, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflow: 'hidden' },
  modalHead: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { fontSize: 17, fontWeight: 800, margin: 0, flex: 1, color: 'var(--text)' },
  help: { fontSize: 12.5, color: 'var(--text-2)', margin: 0, lineHeight: 1.55 },
  form: { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: 12 },
  row: { display: 'flex', gap: 8, alignItems: 'center' },
  input: { background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 10px', color: 'var(--text)', fontSize: 13.5 },
  error: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)', borderRadius: 10, padding: '8px 12px', fontSize: 13 },
  secondary: { background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '7px 12px', fontSize: 12.5, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' },
  primary: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)', border: 'none', borderRadius: 10, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, color: 'var(--on-accent)', cursor: 'pointer' },
  list: { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 60 },
  listItem: { display: 'flex', alignItems: 'flex-start', gap: 6, border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', background: 'var(--surface)' },
};
